import { parseClientDataStore } from '@/utils/parseClientDataStore';
import { tryParseJSON } from '@/utils/tryParseJSON';

export const getUserConsentPromise = () => {
	return new Promise((resolve) => {
		// Check stub first
		if (typeof window.__gpp === 'function') {
			window.__gpp('ping', (pingData) => {
				if (pingData?.gppString) {
					resolve();
				}
			});
		}

		if (typeof window.ketch === 'function') {
			window.ketch('once', 'hideExperienceReasonUpdated', () => {
				resolve();
			});
		} else {
			resolve();
		}
	});
};

export const getPageAdConfig = (pageId) => {
	const { adData = {}, adDataAdc } = window.tagEngineDataLayer;

	if (adDataAdc) {
		return adDataAdc;
	}

	const pageFromPath = window.location.pathname.replace('/app', '').replaceAll('/', '');
	const _pageId = pageId || pageFromPath || 'home';
	return adData[_pageId] || { ...adData['home'], adSlots: {} }; // fallback to home config if specific page config is not found
};

export const setupGlobalAdConfig = ({
	globalAdConfig,
	ppid,
	ortbConfig,
	isPrebidDisabled,
	userData,
	platform,
	hashedEmail,
}) => {
	// Defaults
	const config = {
		prebidTimeout: 750,
		awxTimeout: 10000,
		disableInitialAdLoad: true,
		gptEnableSingleRequest: true,
		gptLazyLoading: '{}',
		buyItNowSkipGoogleAdManager: true,
		isPrebidDisabled,
		userData,
		...globalAdConfig,
	};

	if (ppid) {
		config.ppid = ppid;
	}

	if (ortbConfig) {
		config.ortbConfig = ortbConfig;
	}

	if (platform) {
		config.platform = platform;
	}

	if (hashedEmail) {
		config.hashedEmail = hashedEmail;
		window.eh2 = hashedEmail;
	}

	// Try parse gpt lazy load
	try {
		const gptLazyLoading = tryParseJSON(
			config.gptLazyLoading.replace(
				/(['"])?([a-z0-9A-Z_]+)(['"])?:/g,
				'"$2": ',
			),
			true,
		);
		if (
			typeof gptLazyLoading === 'object' &&
			Object.keys(gptLazyLoading).length > 0
		) {
			config.gptLazyLoading = gptLazyLoading;
		} else {
			delete config.gptLazyLoading;
		}
	} catch (error) {
		window.awxLogger
			?.withScope('Ads::setupGlobalAdConfig')
			.debug('Error parsing gptLazyLoading', error);
		delete config.gptLazyLoading;
	}

	window.globalAdConfig = config;
};

const createTargetingObject = (adInfo) => {
	// targeting object is a subset of adInfo with the values converted to array
	const targetingKeys = new Set([
		'advelvet',
		'alertscategory',
		'alertssource',
		'alertstypeid',
		'country',
		'cuhd',
		'cuhi',
		'cuuv',
		'cuwd',
		'cuwx',
		'dma',
		'fdate',
		'hour',
		'lfs',
		'lfscategory',
		'lfsday',
		'lfsseverity',
		'partner',
		'pgview',
		'pt',
		'realfeel',
		'site',
		'state',
		'wx_seg',
		'ufdb',
	]);

	return Object.entries(adInfo).reduce((acc, [key, value]) => {
		if (targetingKeys.has(key)) {
			acc[key] = value?.split(',');
		}
		return acc;
	}, {});
};

export const setupAdsOnPage = ({ adSlots }) => {
	const adsMap = new Map();

	if (adSlots && Object.values(adSlots).length) {
		const isMobile = Object.values(adSlots)[0].adUnitCode.split('/')[2] !== 'web';
		const order = isMobile
			? [
				'adhesion',
				'top',
				'middle',
				'native',
				'infeed',
				'bottom',
				'oop',
			]
			: [
				'top',
				'top_right',
				'infeed',
				'bottom_right',
				'middle',
				'native',
				'bottom',
				'oop',
			];

		// If user is not premium-plus, add interstitial to load order
		if (window.adInfo.sub !== 'premium-plus') {
			order.push('interstitial');
		}

		const targeting = createTargetingObject(window.adInfo);

		Object.values(adSlots)
			// Sort the ad object in the correct load order
			.sort((a, b) => order.indexOf(a.slot) - order.indexOf(b.slot))

			// Filter out ads that we don't want to load
			.filter((adObject) => {
				const isInLoadOrder = order.indexOf(adObject.slot) > -1;
				const isEnabledAdEngine = adObject.bidders?.some(
					(b) => b.bidder === 'google',
				);
				return isInLoadOrder && isEnabledAdEngine;
			})

			.forEach((adObject) => {
				// Default winner value for adResults
				adObject.winner = { auction: 0 };

				// Get sizes from ad engine
				adObject.sizes = adObject.bidders
					.find((b) => b.bidder === 'google')
					?.params?.sizes.split(',')
					.map((s) => (s.includes('x') ? s.split('x').map(Number) : s));

				// Clean bidders
				adObject.bidders.forEach((b) => {
					for (const [key, value] of Object.entries(b.params)) {
						if (value === '%%targeting%%') {
							b.params[key] = { adunit: [adObject.adUnitCode], ...targeting };
						} else if (typeof value === 'string' && value.includes('%%')) {
							delete b.params[key];
						}
					}
				});

				// Insert ad objects to adsOnPage in order
				adsMap.set(adObject.adDivId, adObject);
			});
	}

	window.adsOnPage = adsMap;
};

export const updatePageView = () => {
	const pageViewKey = 'awx_pv';

	const midnight = new Date();
	midnight.setDate(midnight.getDate() + 1);
	midnight.setHours(0, 0, 0, 0);

	let pageViewItem = tryParseJSON(localStorage.getItem(pageViewKey));

	// If user is still in same day session, increase page view
	if (pageViewItem?.ttl && new Date() < pageViewItem.ttl) {
		pageViewItem.val += 1;
	} else {
		pageViewItem = { val: 1, ttl: +midnight };
	}

	localStorage.setItem(pageViewKey, JSON.stringify(pageViewItem));
};

const convertToF = (x) =>
	roundAwayFromZero(
		parseClientDataStore('userSettings')?.isMetric ? (x * 9) / 5 + 32 : x,
	);
const roundAwayFromZero = (x) => (x < 0 ? -Math.round(-x) : Math.round(x));
const onSetupAdInfo = new CustomEvent('onSetupAdInfo');

export function setupAdInfo({ adInfo = {} }) {
	// TODO: articleid, keywords
	const { ortbConfig, testVariant } = window.globalAdConfig;

	const pageView = tryParseJSON(localStorage.getItem('awx_pv') || '{ "val": 1 }');
	adInfo.pgview = pageView.val.toString();
	adInfo.hour = new Date().getHours().toString();

	const width = document.documentElement.clientWidth;
	const height = document.documentElement.clientHeight;
	const round = (x) => Math.ceil(x / 10) * 10;
	adInfo.viewport = `${round(width)}x${round(height)}`;
	if (adInfo.site === 'adc') {
		adInfo.site = width >= 768
			? (width >= 1024 ? 'desktop' : 'tablet')
			: 'mobile';
	} else {
		adInfo.site = 'one';
	}

	if (ortbConfig?.pageCategories) {
		adInfo.iabctax = ortbConfig.pageCategories.join(',');
	}

	const queryParams = new URLSearchParams(window.location.search);
	if (queryParams.get('adClicks')) {
		adInfo.clicks = queryParams.get('adClicks');
	}

	// parse window.location.pathname for page
	const pathSegments = window.location.pathname.split('/').filter(Boolean);
	if (queryParams.get('from') && pathSegments?.[1].toLowerCase() === 'today') {
		adInfo.from = queryParams.get('from');
		// remove the from param from the url without reloading the page
		const newUrl = window.location.href.replace(
			`?from=${queryParams.get('from')}`,
			'',
		);
		window.history.replaceState({}, document.title, newUrl);
	} else {
		adInfo.from = null;
	}

	// TODO: implement a/b test values
	if (queryParams.get('adtestkey')) {
		adInfo.test = queryParams.get('adtestkey');
	} else if (testVariant) {
		adInfo.test = testVariant;
	}

	adInfo.userid = 'active';

	// Alerts
	const alerts = parseClientDataStore('alerts');
	if (alerts?.length > 0) {
		adInfo.alertscategory = alerts.map((a) => a.category).join(',');
		adInfo.alertssource = alerts.map((a) => a.countryCode).join(',');
		adInfo.alertstypeid = alerts.map((a) => a.typeId).join(',');
	}

	// Current conditions
	const curCon = parseClientDataStore('currentConditions');
	if (curCon) {
		adInfo.cuhd = roundAwayFromZero(curCon.extended?.humidityValue).toString();
		adInfo.cuhi = roundAwayFromZero(curCon.apparentTempImperial).toString();
		adInfo.cuuv = roundAwayFromZero(curCon.extended?.uv?.value).toString();
		adInfo.cuwd = roundAwayFromZero(curCon.windSpeedImperial).toString();
		adInfo.cuwx = curCon.iconCode.toString();
		adInfo.realfeel = roundAwayFromZero(
			convertToF(curCon.realFeelValue),
		).toString();
	}

	// Air quality
	const aq = parseClientDataStore('currentAirQuality');
	if (aq) {
		const plumeIndex = aq.overallPlumeLabsIndex;
		let aqValue = '0';

		if (plumeIndex >= 0 && plumeIndex < 20) {
			aqValue = '1';
		} else if (plumeIndex >= 20 && plumeIndex < 50) {
			aqValue = '2';
		} else if (plumeIndex >= 50 && plumeIndex < 100) {
			aqValue = '3';
		} else if (plumeIndex >= 100 && plumeIndex < 150) {
			aqValue = '4';
		} else if (plumeIndex >= 150 && plumeIndex < 250) {
			aqValue = '5';
		} else if (plumeIndex >= 250) {
			aqValue = '6';
		}

		adInfo.cuaq = aqValue;
	}

	// Daily forecast
	const lettersMap = 'abcd';
	const dailyForecasts = parseClientDataStore('dailyForecasts');
	if (dailyForecasts) {
		dailyForecasts.slice(0, 4).forEach((df, i) => {
			adInfo.realfeel += `,${lettersMap[i]}${roundAwayFromZero(convertToF(df.day.realFeelValue))}`;
		});
	}

	// Local forecast summary
	const lfs = parseClientDataStore('localForecastSummary');
	if (lfs) {
		const category = lfs.category ? lfs.category.replace('/', '-') : 'x';
		const daysUntilEvent =
			Math.abs(new Date(lfs.startDate) - new Date()) / (1000 * 60 * 60 * 24);
		const totalDays = Math.min(Math.round(daysUntilEvent), 10) + 1; // Clamp to 10 days
		adInfo.lfscategory = category;
		adInfo.lfsseverity = lfs.severity.toString();
		adInfo.lfsday = totalDays.toString();
		adInfo.lfs = `${lfs.severity}_${category}_${totalDays}`;
	}

	// Time on page
	adInfo.pt = localStorage.getItem('timeOnPage') || '0';

	window.adInfo = adInfo;
	window.dispatchEvent(onSetupAdInfo);

	// Ad results object
	const prevAdResults = JSON.parse(localStorage.getItem('adResults'));
	if (pageView.val === 1 && prevAdResults) {
		prevAdResults.previousRatio = prevAdResults.filledAds / prevAdResults.possibleAds;
		prevAdResults.filledAds = 0;
		prevAdResults.possibleAds = 0;
	}
	window.adResults = prevAdResults || {};
}
